"use client";

import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight } from "lucide-react";

export function MarkdownGallery({ images }: { images: { src: string; alt?: string }[] }) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Swipe state
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const minSwipeDistance = 40;

  const openLightbox = (index: number) => {
    setCurrentIndex(index);
    setIsOpen(true);
  };

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const showNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    nextImage();
  };

  const showPrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    prevImage();
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === "ArrowRight") {
        nextImage();
      } else if (e.key === "ArrowLeft") {
        prevImage();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, images.length]);

  // Swipe handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      nextImage();
    }
    if (isRightSwipe) {
      prevImage();
    }
  };

  return (
    <>
      {/* Mobile: scrollable horizontal row, Desktop: 2-col grid up to 4 images */}
      <div className="not-prose my-12 -mx-6 flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-4 sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-6 sm:overflow-visible sm:snap-none sm:px-0 sm:pb-0">
        {images.map((img, i) => {
          const isDesktopHidden = i > 3;
          const isDesktopOverlay = i === 3 && images.length > 4;

          return (
            <div
              key={i}
              onClick={() => openLightbox(i)}
              className={`group relative shrink-0 snap-center aspect-video w-[75vw] cursor-pointer overflow-hidden rounded-2xl border border-border bg-muted shadow-sm sm:w-full ${isDesktopHidden ? "sm:hidden" : ""}`}
            >
              <img
                src={img.src}
                alt={img.alt || ""}
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
              />
              {isDesktopOverlay && (
                <div className="absolute inset-0 z-10 hidden items-center justify-center bg-black/60 text-3xl font-medium text-white transition-colors group-hover:bg-black/50 sm:flex">
                  +{images.length - 4}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-[100vw] border-none bg-transparent p-0 shadow-none sm:max-w-5xl [&>button]:bg-black/50 [&>button]:text-white [&>button]:hover:bg-black/70 [&>button]:hover:text-white">
          <div 
            className="relative flex min-h-[50vh] items-center justify-center touch-pan-y"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {images.length > 1 && (
              <button
                onClick={showPrev}
                className="absolute left-2 z-50 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 focus:outline-none sm:left-4"
              >
                <ChevronLeft className="h-6 w-6 sm:h-8 sm:w-8" />
              </button>
            )}
            
            <img
              src={images[currentIndex]?.src}
              alt={images[currentIndex]?.alt || ""}
              className="max-h-[85vh] w-auto rounded-md object-contain shadow-2xl select-none"
              draggable={false}
            />
            
            {images.length > 1 && (
              <button
                onClick={showNext}
                className="absolute right-2 z-50 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 focus:outline-none sm:right-4"
              >
                <ChevronRight className="h-6 w-6 sm:h-8 sm:w-8" />
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
